import { Box, Flex, Image, Separator, Text, VStack } from '@chakra-ui/react'
import { FlyingEmojis } from 'commons/components/moneyPot/FlyingEmojis'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
	title: string
	image_src: string | false
	collector_name: string
	show_balance: boolean
	show_total: boolean
	balance: number
	total_collected: number
	show_description: boolean
	description: string
	/** When set, FlyingEmojis is rendered above the image and behind the card. */
	emoji?: string
	emojiCount?: number
	emojiSize?: number
}

export const HeadingMoneyPot = ({
	title,
	image_src,
	collector_name,
	show_balance,
	show_total,
	balance,
	total_collected,
	show_description,
	description,
	emoji,
	emojiCount = 10,
	emojiSize = 1.1
}: Props) => {
	const { t } = useTranslation()

	const updatedImageSrc = useMemo(() => {
		if (!image_src) return null
		try {
			const url = new URL(image_src)
			const baseUrl = process.env.REACT_APP_POTS_URL || ''
			return `${baseUrl}collect/${url.pathname}`
		} catch {
			return image_src
		}
	}, [image_src])

	return (
		<Box position='relative' w='full'>
			{/* Layer 0: image — centered like the card below */}
			<Box w='full' display='flex' justifyContent='center' mb={{ base: 0, md: '-8px' }}>
				<Box
					w='full'
					maxW={{ md: '780px', base: '100%' }}
					minW={{ md: '780px', base: 'none' }}
					h={{ md: '368px', base: '165px' }}
					border={{ md: '8px solid rgba(255, 255, 255, 0.8)', base: 'none' }}
					overflow='hidden'
					borderRadius='12px'
					background={
						!image_src
							? 'linear-gradient(249.91deg, #fff8f4 0%, #def1ff 48.96%, #fff8f4 100%)'
							: 'transparent'
					}
					position='relative'
					zIndex={0}
				>
					{updatedImageSrc && (
						<Image src={updatedImageSrc} alt={title} w='full' h='full' objectFit='cover' />
					)}
				</Box>
			</Box>
			{/* Layer 1: emojis above image, behind card */}
			{emoji !== '' && (
				<FlyingEmojis emoji={emoji} count={emojiCount} size={emojiSize} zIndex={1} />
			)}
			{/* Layer 2: card (title, balance, etc.) */}
			<Box
				w='full'
				position='relative'
				zIndex={2}
				display='flex'
				justifyContent='center'
				mt={{ md: '-80px', base: '0' }}
			>
				<Box
					maxW='600px'
					w='100%'
					minW={{ md: '600px', base: 'none' }}
					borderRadius={{ base: '12px', md: '12px' }}
					boxShadow='0px 20px 32px 0px rgba(0, 0, 0, 0.02)'
					p={{ base: '24px', md: '40px' }}
					bg='rgba(255, 255, 255, 0.75)'
					backdropFilter='blur(9px)'
				>
					<VStack gap={0} align='center'>
						<Text
							fontFamily='Degular'
							fontSize={{ base: '32px', md: '40px' }}
							fontWeight={700}
							color='#001642'
							textAlign='center'
							lineHeight='53px'
							letterSpacing='-0.03em'
							mb={2}
						>
							{title}
						</Text>
						<Text
							fontFamily="'Montserrat', 'Helvetica Neue', Helvetica, Arial, sans-serif"
							fontWeight={400}
							fontSize='14px'
							color='#697284'
							textAlign='center'
							lineHeight='18px'
							mb={show_description ? 8 : 0}
						>
							{t('phone_form.created_by', { name: collector_name })}
						</Text>
						{show_description && (
							<Text
								fontFamily="'Montserrat', 'Helvetica Neue', 'Helvetica', Arial, sans-serif"
								fontWeight={400}
								lineHeight='normal'
								fontSize='18px'
								textAlign='center'
								mb={2}
								mt={2}
							>
								{description}
							</Text>
						)}
						<Flex align='center' justify='center' w='full' flexDirection='row' gap={4}>
							{show_total && (
								<VStack gap={2} align='center' mt={8}>
									<Text
										fontFamily="'Montserrat', 'Helvetica Neue', 'Helvetica', Arial, sans-serif"
										fontWeight={400}
										fontSize='16px'
										color='#697284'
										textAlign='center'
										lineHeight='120%'
										letterSpacing='-0.16px'
									>
										{t('phone_form.money_collected')}
									</Text>
									<Text
										fontFamily='Degular'
										fontSize={{ base: '32px', md: '40px' }}
										fontWeight={700}
										color='#001642'
										textAlign='center'
										letterSpacing='-1.2px'
										mt={1}
									>
										{t('phone_form.amount_format', {
											value: total_collected.toFixed(2).replace('.', ',')
										})}
									</Text>
								</VStack>
							)}
							{show_total && show_balance && <Separator mt={8} h='68px' orientation='vertical' />}
							{show_balance && (
								<VStack gap={2} align='center' mt={8}>
									<Text
										fontFamily="'Montserrat', 'Helvetica Neue', 'Helvetica', Arial, sans-serif"
										fontWeight={400}
										fontSize='16px'
										color='#697284'
										textAlign='center'
										lineHeight='120%'
										letterSpacing='-0.16px'
									>
										{t('phone_form.money_available')}
									</Text>
									<Text
										fontFamily='Degular'
										fontSize={{ base: '32px', md: '40px' }}
										fontWeight={700}
										color='#001642'
										textAlign='center'
										letterSpacing='-1.2px'
										mt={1}
									>
										{t('phone_form.amount_format', { value: balance.toFixed(2).replace('.', ',') })}
									</Text>
								</VStack>
							)}
						</Flex>
					</VStack>
				</Box>
			</Box>
		</Box>
	)
}
